// src/components/wizard/WizardApp.jsx - ウィザードベースのメインアプリ

import React, { useState, useEffect } from 'react';
import PostWizard from './PostWizard';
import Toast from '../common/Toast';
import SubscriptionManager from '../subscription/SubscriptionManager';
import { addToHistory, markAsPosted } from '../post/PostHistory';

// ユーザーID取得
const getCurrentUserId = () => {
  let userId = localStorage.getItem('userId');
  if (!userId) {
    userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('userId', userId);
  }
  return userId;
};

// 連携解除の本人確認に使うシークレット。
// userId はブラウザで生成される識別子でサーバー側の認証が無いため、
// これが無いと userId を知っているだけで他人の連携情報を削除できてしまう。
const getDisconnectSecret = () => {
  let secret = localStorage.getItem('disconnectSecret');
  if (!secret) {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    secret = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('disconnectSecret', secret);
  }
  return secret;
};

const WizardApp = ({ userPlan: initialPlan, onPlanChange }) => {
  // 状態管理
  const [userPlan, setUserPlan] = useState(initialPlan || 'free');
  const [usage, setUsage] = useState({ count: 0, remaining: 3, limit: 3 });
  const [isLoading, setIsLoading] = useState(false);
  const [isPostingTwitter, setIsPostingTwitter] = useState(false);
  const [isPostingThreads, setIsPostingThreads] = useState(false);

  // SNS接続状態
  const [twitterConnected, setTwitterConnected] = useState(false);
  const [threadsConnected, setThreadsConnected] = useState(false);
  const [twitterUsername, setTwitterUsername] = useState('');
  const [threadsUsername, setThreadsUsername] = useState('');

  // モーダル状態
  const [showAccountSettings, setShowAccountSettings] = useState(false);

  // トースト
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // 初期化
  useEffect(() => {
    checkSnsConnections();
    loadUsage();
  }, []);

  // プラン変更の監視
  useEffect(() => {
    setUserPlan(initialPlan || 'free');
  }, [initialPlan]);

  // 使用状況読み込み
  const loadUsage = () => {
    try {
      const saved = localStorage.getItem('dailyUsage');
      if (saved) {
        const data = JSON.parse(saved);
        const today = new Date().toDateString();
        if (data.date === today) {
          setUsage({ count: data.count || 0, remaining: 3 - (data.count || 0), limit: 3 });
        }
      }
    } catch (e) {
      console.error('Failed to load usage:', e);
    }
  };

  // SNS接続状況確認
  const checkSnsConnections = async () => {
    const userId = getCurrentUserId();

    try {
      // Twitter
      const twitterRes = await fetch('/api/auth/twitter/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      if (twitterRes.ok) {
        const data = await twitterRes.json();
        if (data.connected) {
          setTwitterConnected(true);
          setTwitterUsername(data.username);
        }
      }
    } catch (e) {
      // ローカル確認
      const localTwitter = localStorage.getItem('twitter_connected');
      if (localTwitter === 'true') {
        setTwitterConnected(true);
        setTwitterUsername(localStorage.getItem('twitter_username') || '');
      }
    }

    try {
      // Threads
      const threadsRes = await fetch('/api/auth/threads/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      if (threadsRes.ok) {
        const data = await threadsRes.json();
        if (data.connected) {
          setThreadsConnected(true);
          setThreadsUsername(data.username);
        }
      }
    } catch (e) {
      const localThreads = localStorage.getItem('threads_connected');
      if (localThreads === 'true') {
        setThreadsConnected(true);
        setThreadsUsername(localStorage.getItem('threads_username') || '');
      }
    }
  };

  // プラン更新ハンドラー
  const handlePlanUpdate = () => {
    const plan = localStorage.getItem('userPlan') || 'free';
    setUserPlan(plan);
    if (onPlanChange) {
      onPlanChange(plan);
    }
  };

  // ログアウト
  // localStorage を消すだけだと、当社が預かった SNS のアクセストークンがサーバー側に
  // 残り続けるため、先にサーバー側の認証情報を削除してから画面状態を初期化する。
  const handleLogout = async () => {
    if (!window.confirm('ログアウトしますか？\nSNSとの連携も解除され、保存された認証情報は削除されます。')) {
      return;
    }

    const userId = getCurrentUserId();
    const disconnectSecret = getDisconnectSecret();

    const results = await Promise.all(['twitter', 'threads'].map(async (platform) => {
      try {
        const response = await fetch('/api/disconnect-sns', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, platform, disconnectSecret })
        });
        return response.ok;
      } catch (error) {
        console.error(`Disconnect failed for ${platform}:`, error);
        return false;
      }
    }));

    // 削除に失敗したまま userId を消すと、同じ利用者として再試行できなくなり
    // サーバー側の認証情報が孤立して残る。失敗時はログアウトを中断する。
    if (results.some((ok) => !ok)) {
      showToast('連携情報の削除に失敗しました。時間をおいて再度お試しください', 'error');
      return;
    }

    localStorage.removeItem('userId');
    localStorage.removeItem('disconnectSecret');
    localStorage.removeItem('userPlan');
    localStorage.removeItem('twitter_connected');
    localStorage.removeItem('twitter_username');
    localStorage.removeItem('threads_connected');
    localStorage.removeItem('threads_username');
    localStorage.removeItem('dailyUsage');
    window.location.reload();
  };

  // AI投稿生成
  const handleGenerate = async (prompt, tone) => {
    if (!prompt.trim()) {
      showToast('テーマを入力してください', 'error');
      return null;
    }

    setIsLoading(true);

    try {
      const endpoint = userPlan === 'premium'
        ? '/api/generate-post'
        : '/api/generate-post-shared';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim(), tone, userType: userPlan })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          showToast('本日の無料生成回数を超えました', 'error');
          return null;
        }
        throw new Error(data.error || '生成に失敗しました');
      }

      // 履歴に保存
      addToHistory(data.post, prompt.trim(), false, []);

      // 使用状況更新
      if (data.usage && userPlan === 'free') {
        setUsage({
          count: 3 - (data.usage.remaining || 0),
          remaining: data.usage.remaining || 0,
          limit: 3
        });
      }

      return { post: data.post };

    } catch (error) {
      console.error('Generation error:', error);
      showToast('生成中にエラーが発生しました', 'error');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Twitter投稿
  const handlePostTwitter = async (content) => {
    if (!content) return;
    if (!twitterConnected) {
      showToast('Twitterアカウントを接続してください', 'error');
      return;
    }

    setIsPostingTwitter(true);
    try {
      const userId = getCurrentUserId();
      const response = await fetch('/api/post-to-twitter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, userId })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Twitter投稿に失敗しました');
      }

      markAsPosted(content, 'twitter');

    } catch (error) {
      console.error('Twitter post error:', error);
      showToast('X投稿に失敗しました', 'error');
      throw error;
    } finally {
      setIsPostingTwitter(false);
    }
  };

  // Threads投稿
  const handlePostThreads = async (content) => {
    if (!content) return;
    if (!threadsConnected) {
      showToast('Threadsアカウントを接続してください', 'error');
      return;
    }

    setIsPostingThreads(true);
    try {
      const userId = getCurrentUserId();
      const response = await fetch('/api/post-to-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, userId })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Threads投稿に失敗しました');
      }

      markAsPosted(content, 'threads');

    } catch (error) {
      console.error('Threads post error:', error);
      showToast('Threads投稿に失敗しました', 'error');
      throw error;
    } finally {
      setIsPostingThreads(false);
    }
  };

  // 両方に投稿
  const handlePostBoth = async (content) => {
    const results = { twitter: false, threads: false };

    if (twitterConnected) {
      try {
        await handlePostTwitter(content);
        results.twitter = true;
      } catch (e) {
        // continue
      }
    }

    if (threadsConnected) {
      try {
        await handlePostThreads(content);
        results.threads = true;
      } catch (e) {
        // continue
      }
    }

    return results;
  };

  // Twitter接続
  const handleConnectTwitter = async () => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch('/api/auth/twitter/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, disconnectSecret: getDisconnectSecret() })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authUrl || data.url) {
          window.location.href = data.authUrl || data.url;
        }
      } else {
        showToast('Twitter認証に失敗しました', 'error');
      }
    } catch (error) {
      console.error('Twitter connect error:', error);
      showToast('Twitter接続でエラーが発生しました', 'error');
    }
  };

  // Threads接続
  const handleConnectThreads = async () => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch('/api/auth/threads/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, disconnectSecret: getDisconnectSecret() })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authUrl) {
          window.location.href = data.authUrl;
        }
      } else {
        showToast('Threads認証に失敗しました', 'error');
      }
    } catch (error) {
      console.error('Threads connect error:', error);
      showToast('Threads接続でエラーが発生しました', 'error');
    }
  };

  // アップグレード
  const handleUpgrade = async () => {
    try {
      const userId = getCurrentUserId();
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        showToast('決済画面の作成に失敗しました', 'error');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      showToast('エラーが発生しました', 'error');
    }
  };

  return (
    <>
      <PostWizard
        userPlan={userPlan}
        usage={usage}
        twitterConnected={twitterConnected}
        threadsConnected={threadsConnected}
        twitterUsername={twitterUsername}
        threadsUsername={threadsUsername}
        onGenerate={handleGenerate}
        onPostTwitter={handlePostTwitter}
        onPostThreads={handlePostThreads}
        onPostBoth={handlePostBoth}
        isLoading={isLoading}
        isPostingTwitter={isPostingTwitter}
        isPostingThreads={isPostingThreads}
        onConnectTwitter={handleConnectTwitter}
        onConnectThreads={handleConnectThreads}
        onUpgrade={handleUpgrade}
        onOpenSettings={() => setShowAccountSettings(true)}
        onLogout={handleLogout}
        showToast={showToast}
      />

      {/* アカウント設定モーダル */}
      {showAccountSettings && (
        <SubscriptionManager
          userId={getCurrentUserId()}
          onPlanChange={handlePlanUpdate}
          onClose={() => setShowAccountSettings(false)}
        />
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </>
  );
};

export default WizardApp;
